/* eslint-disable */
/**
 * Assembles a final branded email from an EmailChrome config + a composed body.
 *
 * The header, optional facts strip, optional CTA and footer come from `chrome`
 * and are NOT editable by the body author — this guarantees every email of a
 * given kind carries the brand and required facts even if someone deletes them
 * from their free-text body. Layout uses HTML tables + inline styles throughout,
 * which is what email clients actually require (no flexbox, no external CSS).
 *
 * The body is always sanitized here — callers never need to remember to
 * sanitize separately, and can't accidentally skip it. `bodyMode` picks which
 * pipeline: 'editor' (default) canonicalises via sanitizeEmailHtml; 'imported'
 * preserves the file's own markup via sanitizeImportedEmailHtml.
 *
 * `includeChrome` controls whether the standard scaffolding wraps the body.
 * It defaults per mode: editor bodies always get the chrome; imported bodies
 * ship as-is (a file import is a complete email with its own branding) unless
 * the author explicitly opts back in with includeChrome: true.
 */

import { sanitizeEmailHtml } from "./sanitizer";
import { sanitizeImportedEmailHtml } from "./validateTemplate";
import { resolvePlaceholders, resolvePlaceholdersInHtml } from "./placeholders";
import { ParseHtml } from "./domAdapter";
import { AssembledEmail, EmailChrome, EmailChromeFooter } from "./types";

export interface AssembleEmailOptions {
  chrome: EmailChrome;
  subject: string;
  /** Editor/raw HTML — sanitized internally, so this can be untrusted input. */
  bodyHtml: string;
  /** 'editor' (default) canonicalises the body to house style; 'imported' keeps a full-copy body (from an HTML file import) intact — security pass only. */
  bodyMode?: "editor" | "imported";
  /** Wrap the body in the standard header/facts/CTA/footer. Defaults per mode: true for editor bodies, false for imported ones. */
  includeChrome?: boolean;
  /** Values for {{placeholder}} tokens in subject, chrome.footer.note, chrome.factsRows, chrome.cta.url, and the body. */
  values?: Record<string, string>;
  /** Node callers pass an adapter here (see domAdapter.ts); browsers can omit it. */
  parseHtml?: ParseHtml;
}

export function assembleEmail(opts: AssembleEmailOptions): AssembledEmail {
  const { chrome, values = {}, parseHtml } = opts;
  const subject = resolvePlaceholders(opts.subject, values);
  const cleanBody =
    opts.bodyMode === "imported"
      ? sanitizeImportedEmailHtml(opts.bodyHtml, { parseHtml }).html
      : sanitizeEmailHtml(opts.bodyHtml, { parseHtml });
  const bodyHtml = resolvePlaceholdersInHtml(cleanBody, values);
  const accentColor = chrome.brand.accentColor;

  // Imported files are complete emails with their own branding — by default they
  // ship as-is in a bare shell instead of nesting inside the standard chrome.
  // includeChrome overrides the per-mode default in either direction.
  const withChrome = opts.includeChrome ?? opts.bodyMode !== "imported";
  if (!withChrome) {
    const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escapeHtmlText(subject)}</title>
</head>
<body style="margin:0;padding:0;">
${bodyHtml}
</body>
</html>`;
    return { subject, html };
  }

  const footerHtml = renderFooter(chrome.footer, values, accentColor);
  const ctaUrl = chrome.cta
    ? resolvePlaceholders(chrome.cta.url, values)
    : null;

  const logoCell = chrome.brand.logoUrl
    ? `<img src="${escapeAttr(chrome.brand.logoUrl)}" alt="${escapeAttr(
        chrome.brand.name
      )}" width="40" height="40" style="display:block;border-radius:10px;">`
    : `<span style="font-size:15px;font-weight:700;color:#111827;">${escapeHtmlText(
        chrome.brand.logoInitial || chrome.brand.name.slice(0, 1)
      )}</span>`;

  const factsRow = chrome.factsRows?.length
    ? `<tr><td style="background:#F9FAFB;border-bottom:1px solid #E5E7EB;">
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr>
          ${chrome.factsRows
            .map((f, i) =>
              factCell(
                resolvePlaceholders(f.label, values),
                resolvePlaceholders(f.value, values),
                i < chrome.factsRows!.length - 1
              )
            )
            .join("")}
        </tr></table>
      </td></tr>`
    : "";

  const ctaRow =
    chrome.cta && ctaUrl
      ? `<tr><td align="center" style="padding:8px 28px 26px;">
        <a href="${escapeAttr(
          ctaUrl
        )}" style="display:inline-block;background:${
          chrome.brand.accentColor
        };color:#FFFFFF;font-size:14px;font-weight:600;line-height:20px;padding:12px 28px;border-radius:8px;text-decoration:none;">${escapeHtmlText(
          chrome.cta.label
        )}</a>
      </td></tr>`
      : "";

  // An uploaded "powered by" badge image always wins over the plain-text label.
  const poweredBy = chrome.poweredByLogoUrl
    ? `<img src="${escapeAttr(chrome.poweredByLogoUrl)}" alt="${escapeAttr(
        chrome.poweredByLabel || "Powered by"
      )}" style="display:inline-block;height:14px;vertical-align:middle;border:0;">`
    : chrome.poweredByLabel
    ? `<span style="font-size:10px;color:#9CA3AF;">powered by</span><span style="font-size:10px;color:${
        chrome.brand.accentColor
      };font-weight:600;">&nbsp;${escapeHtmlText(chrome.poweredByLabel)}</span>`
    : "";

  const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escapeHtmlText(subject)}</title>
</head>
<body style="margin:0;padding:24px 12px;background:#F3F4F6;font-family:'Inter',Arial,Helvetica,sans-serif;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr><td align="center">
    <table role="presentation" width="600" cellpadding="0" cellspacing="0" style="max-width:600px;width:100%;background:#FFFFFF;border-radius:16px;border:1px solid #E5E7EB;overflow:hidden;">

      <tr><td style="background:${chrome.brand.darkColor};padding:22px 28px;">
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr>
          <td>
            <table role="presentation" cellpadding="0" cellspacing="0"><tr>
              <td style="width:40px;height:40px;background:#FFFFFF;border-radius:10px;text-align:center;vertical-align:middle;">${logoCell}</td>
              <td style="padding-left:14px;">
                <div style="font-size:15px;font-weight:600;color:#FFFFFF;letter-spacing:-0.2px;">${escapeHtmlText(
                  chrome.brand.name
                )}</div>
                <div style="font-size:11px;color:#9CA3AF;margin-top:1px;">${escapeHtmlText(
                  chrome.headerSubtitle
                )}</div>
              </td>
            </tr></table>
          </td>
          <td align="right" style="vertical-align:middle;">${poweredBy}</td>
        </tr></table>
      </td></tr>

      ${factsRow}

      <tr><td style="padding:24px 28px 8px;">
        ${bodyHtml}
      </td></tr>

      ${ctaRow}

      ${
        footerHtml
          ? `<tr><td style="padding:14px 28px;border-top:1px solid #F1F5F9;">${footerHtml}</td></tr>`
          : ""
      }

    </table>
  </td></tr></table>
</body>
</html>`;

  return { subject, html };
}

function factCell(label: string, value: string, withBorder: boolean): string {
  return `
    <td style="padding:12px 16px;${
      withBorder ? "border-right:1px solid #E5E7EB;" : ""
    }">
      <div style="font-size:10px;font-weight:600;letter-spacing:0.06em;text-transform:uppercase;color:#9CA3AF;">${escapeHtmlText(
        label
      )}</div>
      <div style="font-size:13px;font-weight:600;color:#111827;margin-top:2px;">${escapeHtmlText(
        value
      )}</div>
    </td>`;
}

/** Self-contained circular icon (no external image host / no unverifiable third-party URL) for the footer social row. */
function socialIconDataUri(glyph: string, bg: string): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28"><circle cx="14" cy="14" r="14" fill="${bg}"/><text x="14" y="19" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="12" font-weight="700" fill="#FFFFFF">${glyph}</text></svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

const LINKEDIN_ICON = socialIconDataUri("in", "#0A66C2");
const X_ICON = socialIconDataUri("X", "#000000");

/**
 * Renders the footer from plain-text/URL fields only — callers never supply
 * HTML here. Everything is centered and reads as one cohesive block:
 * `learnMore` (if set) leads as a prominent accent-colored pill — deliberately
 * louder than everything below it, since its whole job is to get noticed and
 * clicked — then the confidentiality `note`, then a short centered rule, then
 * the muted copyright/trademark/unsubscribe/social group.
 */
function renderFooter(
  footer: EmailChromeFooter | undefined,
  values: Record<string, string>,
  accentColor: string
): string {
  if (!footer) return "";

  const learnMoreBlock = footer.learnMore?.url
    ? `<div style="margin-bottom:16px;">
        <a href="${escapeAttr(
          resolvePlaceholders(footer.learnMore.url, values)
        )}" style="display:inline-block;font-size:12px;font-weight:600;line-height:16px;color:${accentColor};background:#FFFFFF;border:1.5px solid ${accentColor};border-radius:20px;padding:8px 16px;text-decoration:none;">
          ${escapeHtmlText(
            resolvePlaceholders(footer.learnMore.label, values)
          )} &nbsp;→
        </a>
      </div>`
    : "";

  const noteText = footer.note ? resolvePlaceholders(footer.note, values) : "";
  const noteBlock = noteText
    ? `<div style="max-width:400px;margin:0 auto;">${escapeHtmlText(
        noteText
      ).replace(/\n/g, "<br>")}</div>`
    : "";

  const legalLines: string[] = [];
  if (footer.copyrightText) {
    legalLines.push(
      `<div style="font-style:italic;">${escapeHtmlText(
        footer.copyrightText
      )}</div>`
    );
  }
  if (footer.trademarkText) {
    legalLines.push(
      `<div style="margin-top:6px;">${escapeHtmlText(
        footer.trademarkText
      )}</div>`
    );
  }
  if (footer.unsubscribe?.url) {
    legalLines.push(
      `<div style="margin-top:6px;">If you would like to stop receiving emails of this sort in the future, click on ` +
        `<a href="${escapeAttr(
          footer.unsubscribe.url
        )}" style="color:#185FA5;text-decoration:underline;">${escapeHtmlText(
          footer.unsubscribe.label || "Unsubscribe"
        )}</a></div>`
    );
  }

  const socialIcons: string[] = [];
  if (footer.social?.linkedinUrl) {
    socialIcons.push(
      `<a href="${escapeAttr(
        footer.social.linkedinUrl
      )}" style="display:inline-block;margin:0 5px;text-decoration:none;"><img src="${LINKEDIN_ICON}" width="28" height="28" alt="LinkedIn" style="display:inline-block;border:0;"></a>`
    );
  }
  if (footer.social?.xUrl) {
    socialIcons.push(
      `<a href="${escapeAttr(
        footer.social.xUrl
      )}" style="display:inline-block;margin:0 5px;text-decoration:none;"><img src="${X_ICON}" width="28" height="28" alt="X (Twitter)" style="display:inline-block;border:0;"></a>`
    );
  }
  const socialRow = socialIcons.length
    ? `<div style="margin-top:16px;">${socialIcons.join("")}</div>`
    : "";

  const legalBlock =
    legalLines.length || socialRow
      ? `<div>${legalLines.join("")}${socialRow}</div>`
      : "";

  const topBlock = learnMoreBlock + noteBlock;
  const divider =
    topBlock && legalBlock
      ? `<div style="width:28px;height:1px;background:#E5E7EB;margin:16px auto;font-size:0;line-height:0;">&nbsp;</div>`
      : "";

  const content = topBlock + divider + legalBlock;
  return content
    ? `<div style="font-size:11px;line-height:18px;color:#9CA3AF;text-align:center;">${content}</div>`
    : "";
}

function escapeHtmlText(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}
function escapeAttr(text: string): string {
  return escapeHtmlText(text).replace(/"/g, "&quot;");
}
